import { memo, useCallback, useMemo, useRef, UIEvent } from "react";
import { type RowComponentProps } from 'react-window';
import classNames from "classnames";
import { PropsWithChildren, useEffect, useState } from "react";
import styles from "./bunchGrid.module.scss"
import defaultStyles from "../../../../../Business/styles/index.module.scss";
import { DateTime } from "luxon";
import { } from '../../../../../Services/Extensions/Boolean'
import { } from '../../../../../Services/Extensions/DateTime'
import { GridRowHeightEnum, LoadingStatus } from "../../../../../@types/enumsGlobal";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid'
import { useAppContext } from "../../../../../system/providers/appContextProvider";
import { useUserContext } from "../../../../../system/providers/userContextProvider";
import { DownIcon, SortIcon, UpIcon } from "../../../../../libs/corporate/icons/outlined/directions/ChevronCollection";
import GridPaginator from "../../../../../components/grids/gridPaginator";
import { ContextMenu } from "../../../../../components/contextMenu/contextMenu";
import GridUISettings from "../../../../../components/grids/default/gridUiSettings";
import { isArray, orderBy } from "lodash";
import FlexRow from "../../../../../components/controls/FlexRow";
import { CheckBox } from "../../../../../components/controls/checkbox";
import { VirtualGrid } from "./VirtualGrid";
import { GridHeader } from "./GridHeader";
import { OverflowTooltip } from "./components/OverflowTooltip";

interface IGridCellWithWidth extends IGridCell {
    width: number
}

function compareByOrder(a: IComparer, b: IComparer) {
    if (a.order === undefined || b.order === undefined)
        return 0;

    if (a.order < b.order)
        return -1;

    if (a.order > b.order)
        return 1;

    return 0;
}

interface IMousePosition {
    x: number
    y: number
}

interface IColumnToSave {
    columns: IGridColumn[];
    save: boolean;
}

interface ISearchRows {
    value: string;
    order: number;
}

interface IDefaultGridProps {
    gridId: string;
    plugin: IPluginSettings;
    data: any[];
    filter: IGridFilter;
    totalCount: number;
    multipleSelect?: boolean,
    selectedItem?: IGridRow | IGridRowWithMultipleValue;
    selectedItems?: IGridRow[];
    hiddenPagination?: boolean;
    numbering?: boolean;
    contextMenuItems?: IContextMenuItem[];
    loadingStatus?: LoadingStatus;
    hideSettingstButton?: boolean;
    setLoadingData?: (value: LoadingStatus) => void;
    onSelect?: (row: IGridRow | IGridRowWithMultipleValue | undefined) => void;
    onDoubleClick?: (row: IGridRow) => void;
    onEnter?: (row: IGridRow) => void;
    onMultipleSelect?: (rows: IGridRow[]) => void;
    singleDirectory?: boolean;
    separator?: boolean;
    searching?: boolean;
    autoSelect?: boolean;
    useUiService?: boolean;
    setPluginSettings?: (plugin: IPluginSettings) => void;
    onSort: (column: IGridColumn, data?: any[]) => void;
    onFilterDelete: (column: IGridColumn) => void;
    onPageNumberChange: (pageNumber: number) => void;
    onNumberPerPageChange: (numberPerPage: number) => void;
    onGridRowContextMenuMouseClick?: (selectedRow: IGridRow, position: IMousePosition) => JSX.Element;
    renderCell?: (cell: IGridCellWithWidth, row: IGridRow, additionalData?: any) => React.ReactNode;
}
interface IRowComponentData {
    rows: IGridRow[];
    pluginSettings: IPluginSettings;
    multipleSelect?: boolean;
    numbering?: boolean;
    selectedItem?: IGridRow | IGridRowWithMultipleValue;
    selectedItems?: IGridRow[];
    onRowClick: (item: IGridRow) => void;
    onRowDoubleClick: (item: IGridRow) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>, item: IGridRow) => void;
    onContextMenu: (e: React.MouseEvent<HTMLTableRowElement>, item: IGridRow) => void;
    renderCell?: (cell: IGridCellWithWidth, row: IGridRow, additionalData?: any) => React.ReactNode;
    getStyleTd: (width: string, minWidth: string) => React.CSSProperties;
    styles: typeof styles;
    setSelectedItem: (item: IGridRow | undefined) => void;
    onSelect?: (row: IGridRow | IGridRowWithMultipleValue | undefined) => void;
    onMultipleSelect?: (rows: IGridRow[]) => void;
}

const FIXED_ROW_HEIGHT = 44;

const BunchGrid = (props: PropsWithChildren<IDefaultGridProps>) => {
    const appCtx = useAppContext();
    const userCtx = useUserContext();

    const [gridRowContextMenuVisible, setGridRowContextMenuVisible] = useState(false);
    const [gridHeaderContextMenuVisible, setGridHeaderContextMenuVisible] = useState(false);
    const [showGridHeaderSettings, setShowGridHeaderSettings] = useState(false);
    const [gridRowContextMenu, setGridRowContextMenu] = useState<JSX.Element | undefined>();
    const [headerCtxPosY, setHeaderCtxPosY] = useState(0);
    const [headerCtxPosX, setHeaderCtxPosX] = useState(0);
    const [allChecked, setAllChecked] = useState<boolean>(false);
    const [typeChecked, setTypeChecked] = useState<string>('emptyField');
    const [useUiService] = useState<boolean>(props.useUiService ?? true);

    const [, setPluginSettingsLoaded] = useState<boolean>(false);
    const rowHeight = FIXED_ROW_HEIGHT;
    const [pluginSettings, setPluginSettings] = useState<IPluginSettings>({
        ...props.plugin,
        rowHeight: rowHeight
    });

    const [gridColumnsToSave, setGridColumnsToSave] = useState<IColumnToSave>({ columns: [], save: false });
    const [data, setData] = useState<any[]>(props?.data?.map(x => ({ ...x, idRow: uuidv4() })));
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined | IGridRowWithMultipleValue>();
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [firstRender, setFirstRender] = useState<boolean>(false);
    const [loadingRow, setLoadingRow] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [onlyСonnected, setOnlyСonnected] = useState<boolean>(false);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const yDividerPos = useRef<number>(0);
    const [clientHeight, setClientHeight] = useState<number | null>(getClientHeight(data.length));
    const [scrollLeft, setScrollLeft] = useState(0);
    const [searchRows, setSearchRows] = useState<ISearchRows>({ value: '', order: 0 });
    const [searchRowsArray, setSearchRowsArray] = useState<ISearchRows[]>([]);
    const [toggleSortDate, setToggleSortDate] = useState<boolean>(false);

    const [sortingStatus, setSortingStatus] = useState<boolean>(true);
    const [initialLocalSortingData, setInitialLocalSortingData] = useState({
        fieldOrderBy: "",
        orderDesc: true,
    })

    const { t } = useTranslation();
    const baseT = (value: string) => t('grids.' + value);

    const totalTableWidth = useMemo(() => {
        return pluginSettings.columns
            .filter(c => c.visibility)
            .reduce((sum, c) => sum + (c.width ?? 0), 0)
            + (props.multipleSelect ? 45 : 0)
            + (props.numbering ? 45 : 0);
    }, [pluginSettings.columns, props.multipleSelect, props.numbering]);

    const columnsKey = useMemo(() => {
        return JSON.stringify(
            pluginSettings.columns.map(c => ({
                propertyName: c.propertyName,
                order: c.order,
                visibility: c.visibility,
                propertyType: c.propertyType
            }))
        );
    }, [pluginSettings.columns]);

    const selectedItemsKey = useMemo(() => {
        return props.selectedItems?.map(x => x.idRow).join(',') || '';
    }, [props.selectedItems]);

    const handleColumnReorder = useCallback((updatedColumns: IGridColumn[]) => {
        setPluginSettings(prev => ({
            ...prev,
            columns: updatedColumns
        }));

        setTimeout(() => {
            const table = document.getElementById(props.gridId);
            if (!table) return;

            updatedColumns.forEach(column => {
                if (!column.visibility) return;

                const headerCell = table.querySelector(
                    `thead tr td[data-property-name="${column.propertyName}"]`
                ) as HTMLElement;

                if (headerCell) {
                    const width = headerCell.offsetWidth;
                    const allDataCells = table.querySelectorAll(
                        `tbody tr td[data-property-name="${column.propertyName}"]`
                    );

                    allDataCells.forEach(cell => {
                        const htmlCell = cell as HTMLElement;
                        htmlCell.style.width = `${width}px`;
                        htmlCell.style.minWidth = `${width}px`;
                    });
                }
            });
        }, 50);
    }, [props.gridId]);

    function sortArrayByField(items: any[], field: string, orderDesc: boolean) {
        return orderBy(items, [field], [orderDesc ? 'desc' : 'asc']);
    }

    function getClientHeight(dataLength: number, rowHeight: number = pluginSettings?.rowHeight ?? GridRowHeightEnum.normal) {
        if (dataLength >= 10 && props.singleDirectory) {
            const height = (props.filter.numberPerPage - 1) * 44;
            return height;
        } else if (dataLength >= 10 && !props.singleDirectory) {
            return 480;
        } else if (dataLength === 1) {
            return 100;
        } else if (dataLength === 0) {
            return 380;
        } else {
            return dataLength * 47 + 50;
        }
    }

    const onMouseHoldDown = (e) => {
        yDividerPos.current = e.clientY;
    };

    const onMouseHoldUp = () => {
        yDividerPos.current = 0;
    };

    const onMouseHoldMove = (e) => {
        if (!yDividerPos.current) {
            return;
        }
        setClientHeight(clientHeight + e.clientY - yDividerPos.current);
        yDividerPos.current = e.clientY;
    };

    const handleGridScroll = (e: UIEvent<HTMLDivElement>) => {
        setScrollLeft(e.currentTarget.scrollLeft);
    };

    useEffect(() => {
        let newArr: ISearchRows[] = [];
        for (let i = 0; i < searchRowsArray.length; i++) {
            if ((searchRowsArray[i].order !== searchRows.order) && (searchRowsArray[i].value !== '')) {
                newArr.push(searchRowsArray[i]);
            }
        }
        if (searchRows.value !== '') {
            newArr.push({ value: searchRows.value, order: searchRows.order });
        }
        setSearchRowsArray(([...newArr]));
    }, [searchRows]);

    useEffect(() => {
        if (toggleSortDate === false) return;
        if (sortingStatus) {
            setData(data?.sort((a, b) => {
                if (a?.dateCreated === "0001-01-01T00:00:00" || b?.dateCreated === "0001-01-01T00:00:00") return 0
                return a?.dateCreated?.toSeconds() - b?.dateCreated?.toSeconds()
            }).reverse()
            );
        }
    }, [data, sortingStatus]);

    useEffect(() => {
        if (!toggleSortDate || !data || data.length === 0) return;

        const sortedData = [...data].sort((a, b) => {
            if (a.connected === b.connected) return 0;
            return a.connected ? 1 : -1;
        });

        setData(sortedData);
    }, [toggleSortDate, props.data]);

    useEffect(() => {
        document.addEventListener("mouseup", onMouseHoldUp);
        document.addEventListener("mousemove", onMouseHoldMove);
        return () => {
            document.removeEventListener("mouseup", onMouseHoldUp);
            document.removeEventListener("mousemove", onMouseHoldMove);
        };
    });

    useEffect(() => {
        if (props.separator) {
            if (!clientHeight) {
                setClientHeight(getClientHeight(data.length));
                return;
            }
            if (tableRef.current) {
                tableRef.current.style.minHeight = clientHeight + "px";
                tableRef.current.style.maxHeight = clientHeight + "px";
            }
        }
    }, [clientHeight, tableRef.current]);

    useEffect(() => {
        setData(props?.data?.map(x => ({ ...x, idRow: uuidv4() })))
    }, [props.data])

    useEffect(() => {
        pluginSettingsLoad();
    }, [])

    useEffect(() => setSelectedItem(props.selectedItem), [props.selectedItem])

    useEffect(() => {

        if ((props.autoSelect && data.length === 1) || (data.length > 0 && !firstRender)) {
            rows.map((item, index) => {
                const cells = item.cells
                let orderedCells: IGridCellWithWidth[] = [];
                for (let i = 0; i < cells.length; i++) {
                    orderedCells.push({
                        ...cells[i],
                        order: pluginSettings.columns.find((item) => item.propertyName === cells[i].propertyName)?.order as number,
                        width: pluginSettings.columns.find((item) => item.propertyName === cells[i].propertyName)?.width as number
                    });
                }
                orderedCells = orderedCells.sort(compareByOrder)
                if (index === 0) {
                    props.onSelect?.(item);
                    setSelectedItem(item);
                    data.length > 0 && !firstRender && setFirstRender(true);
                }
            })
        }
    }, [data])

    const savePluginSettingsToServer = useCallback((columnsToSave: IGridColumn[], callback?: () => void) => {
        if (!useUiService) return;

        appCtx.uiApiService.uiPluginSettingsRequest.save(
            {
                idUser: userCtx.idGlobal,
                name: pluginSettings.name,
                mnemocode: pluginSettings.mnemocode,
                gridColumns: columnsToSave,
                detailsPlugins: [] as string[],
                rowHeight: rowHeight,
            },
            () => {
                if (callback) callback();
            }
        );
    }, [useUiService, appCtx, userCtx, pluginSettings.name, pluginSettings.mnemocode, pluginSettings.rowHeight]);

    const pluginSettingsLoad = () => {
        if (useUiService) {
            setLoadingRow(LoadingStatus.InProcess);
            appCtx.uiApiService.uiPluginSettingsRequest.load(pluginSettings.mnemocode, userCtx.idGlobal, (e) => {
                if (e.respType === "isCompleted") {
                    let columns: IGridColumn[] = [];
                    e.data.gridColumns.forEach((item) => columns.push(item));
                    const plugin: IPluginSettings = {
                        name: e.data.name,
                        mnemocode: e.data.mnemocode,
                        columns: [...columns],
                        permission: pluginSettings.permission,
                        rowHeight: rowHeight,
                    };
                    setPluginSettings({ ...plugin });
                    props?.setPluginSettings?.({ ...plugin });
                    setPluginSettingsLoaded(true);
                    setClientHeight(getClientHeight(data.length, rowHeight));
                    setLoadingRow(LoadingStatus.Completed);
                } else {
                    savePluginSettingsToServer(pluginSettings.columns, () => {
                        setClientHeight(getClientHeight(data.length, pluginSettings.rowHeight));
                        setPluginSettingsLoaded(true);
                        setLoadingRow(LoadingStatus.Completed);
                    });
                }
            });
        } else {
            setLoadingRow(LoadingStatus.InProcess);
            setClientHeight(getClientHeight(data.length));
            setLoadingRow(LoadingStatus.Completed);
        }
    };

    useEffect(() => {
        pluginSettingsLoad();
    }, []);

    useEffect(() => {
        if (useUiService && gridColumnsToSave.save) {
            savePluginSettingsToServer(gridColumnsToSave.columns, () => {
                setPluginSettings(prev => ({
                    ...prev,
                    columns: gridColumnsToSave.columns
                }));
                props?.setPluginSettings?.({ ...pluginSettings, columns: gridColumnsToSave.columns });
                setGridColumnsToSave(prev => ({ ...prev, save: false }));
                setClientHeight(getClientHeight(data.length, pluginSettings.rowHeight));
            });
        }
    }, [gridColumnsToSave]);

    useEffect(() => {
        if (allChecked) {
            let multipleiArr: IGridRow[] = []
            rows.map(i => {
                multipleiArr.push(i)
                props.onMultipleSelect?.([...multipleiArr])
                return i

            })
        } else {
            let multipleiArr: IGridRow[] = []
            rows.map(i => {
                multipleiArr.push(i)
                props.onMultipleSelect?.([])
                return i

            })
        }
    }, [allChecked])

    const rows = useMemo(() => {
        if (!data || !pluginSettings?.columns) return [];

        const columnMap = new Map(
            pluginSettings.columns.map(col => [col.propertyName, col])
        );

        const result: IGridRow[] = [];

        data.forEach((item) => {
            const createIdGlobal = Array.isArray(item["idGlobal"]) ? item["idGlobal"][0] : item["idGlobal"];
            const cells: IGridCell[] = [];

            Object.entries(item).forEach(([propertyName, rawValue]) => {
                try {
                    const column = columnMap.get(propertyName);
                    if (!column) return;

                    let value = rawValue;

                    if (value && value !== null && value !== '') {
                        switch (column.propertyType) {
                            case 'boolean':
                                value = value as boolean;
                                break;
                            case 'date':
                                if (typeof value !== 'string' && value instanceof DateTime) {
                                    value = value.toF3DateFormat(userCtx);
                                } else {
                                    value = null;
                                }
                                break;
                            case 'datetime':
                                if (typeof value !== 'string' && value instanceof DateTime) {
                                    value = value.toF3DateTimeFormat(userCtx);
                                } else {
                                    value = null;
                                }
                                break;
                        }
                    }

                    cells.push({
                        value,
                        propertyName,
                        order: column.order as number,
                        multipleValue: isArray(value),
                        visibility: column.visibility ?? true
                    });
                } catch (ex) {
                    console.trace(`${propertyName} -> ${rawValue}`, ex);
                }
            });

            const sortedCells = cells
                .filter(cell => cell.order !== undefined && cell.order !== null)
                .sort(compareByOrder);

            if (props?.multipleSelect) {
                result.push({
                    idGlobal: item["idGlobal"] as string,
                    idRow: item['idRow'] as string,
                    displayName: item["displayName"] as string,
                    cells: sortedCells,
                    isDeleted: item["deleted"] as boolean,
                    isSelected: props.selectedItems?.some(x => x.idRow === item.idRow) ?? false,
                    bunchType: item.bunchType as string
                });
            } else {
                result.push({
                    idGlobal: createIdGlobal,
                    idRow: item['idRow'] as string,
                    displayName: (item["displayName"] || item["name"]) as string,
                    cells: sortedCells,
                    isDeleted: item["deleted"] as boolean,
                    isSelected: createIdGlobal === selectedItem?.idGlobal,
                    success: item["success"] as boolean,
                    bunchType: item.bunchType as string
                });
            }
        });

        return result;
    }, [data, columnsKey, userCtx, props?.multipleSelect, selectedItemsKey, selectedItem?.idGlobal]);

    const filteredRows = useMemo(() => {
        if (!rows) return [];

        let result = rows;

        if (searchRowsArray.length > 0) {
            result = result.filter(element => {
                return searchRowsArray.every(searchItem => {
                    const cell = element.cells[searchItem.order];
                    if (!cell) return false;

                    const value = cell.value;
                    const searchValue = searchItem.value.toLowerCase();

                    if (Array.isArray(value)) {
                        return value.some(item =>
                            String(item).toLowerCase().includes(searchValue)
                        );
                    }

                    return String(value).toLowerCase().includes(searchValue);
                });
            });
        }

        if (onlyСonnected) {
            result = result.filter(element => !element.success);
        }

        return result;
    }, [rows, searchRowsArray, onlyСonnected]);

    useEffect(() => {
        const selectedRows = rows.filter(x => x.isSelected)
        const rowsLength = rows.length

        if (selectedRows.length === rowsLength)
            setTypeChecked('allChecked')

        else if (selectedRows.length === 0)
            setTypeChecked('emptyField')

        else if (selectedRows.length < rowsLength)
            setTypeChecked('indeterminate')

    }, [rows])

    useEffect(() => {
        if (filteredRows.length > 0) {
            setTimeout(() => {
                const firstRow = document.querySelector(`tr[data-item-id="${filteredRows[0]?.idGlobal}"]`) as HTMLElement;
                if (firstRow) {
                    firstRow.focus();
                }
            }, 200);
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!['ArrowUp', 'ArrowDown', 'Enter', 'Tab'].includes(e.key)) {
                return;
            }

            const activeElement = document.activeElement;

            let currentRow = activeElement?.closest?.('tr[data-item-id]') as HTMLElement;

            if (!currentRow) {
                const gridElement = gridContainerRef.current;

                if (!gridElement) return;

                currentRow = gridElement.querySelector('tr[data-selected="true"]') as HTMLElement;

                if (!currentRow) {
                    currentRow = gridElement.querySelector('tr[data-item-id]') as HTMLElement;
                }
                if (!currentRow) return;
                currentRow.focus();
                setTimeout(() => {
                    handleKeyDown(e);
                }, 10);
                return;
            }

            const currentId = currentRow.dataset.itemId;

            const currentIndex = filteredRows.findIndex(x => x.idGlobal === currentId);

            if (currentIndex === -1) return;

            let newIndex = currentIndex;
            if (e.key === 'ArrowDown' || e.key === 'Tab') {
                newIndex = currentIndex + 1;
            } else if (e.key === 'ArrowUp') {
                newIndex = currentIndex - 1;
            } else if (e.key === 'Enter') {
                const item = filteredRows[currentIndex];
                if (item && props.onEnter) {
                    e.preventDefault();
                    props.onEnter(item);
                }
                return;
            }

            if (newIndex >= 0 && newIndex < filteredRows.length) {
                e.preventDefault();
                e.stopPropagation();

                const newItem = filteredRows[newIndex];

                setActiveIndex(newIndex);
                setSelectedItem(newItem);
                props.onSelect?.(newItem);

                setTimeout(() => {
                    const newRow = document.querySelector(`tr[data-item-id="${newItem.idGlobal}"]`) as HTMLElement;
                    if (newRow) newRow.focus();
                }, 10);
            }
        };

        document.addEventListener('keydown', handleKeyDown, { passive: false });
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [filteredRows, props.onSelect, props.onEnter]);

    function getContextId(type: 'header' | 'row', baseId: string) {
        return `ctxMenu_${type}_${baseId}`
    }

    useEffect(() => {
        const handler = () => setGridHeaderContextMenuVisible(false)

        if (gridHeaderContextMenuVisible === true) {
            let ctxMenu = document.getElementById(getContextId('header', props.gridId)) as HTMLElement
            if (ctxMenu) {
                ctxMenu.onmouseleave = handler;
                return ctxMenu.removeEventListener("mouseleave", handler)
            }
        }
    }, [gridHeaderContextMenuVisible])

    useEffect(() => {
        const handler = () => setGridRowContextMenuVisible(false)
        if (gridRowContextMenuVisible === true) {
            let ctxMenu = document.getElementById(getContextId('row', props.gridId)) as HTMLElement
            if (ctxMenu) {
                ctxMenu.onmouseleave = handler;
                return ctxMenu.removeEventListener("mouseleave", handler)
            }
        }
    }, [gridRowContextMenuVisible])



    useEffect(() => {
        if (gridRowContextMenu) {
            let ctxMenu = document.getElementById(getContextId('row', props.gridId)) as HTMLElement

            if (ctxMenu) {
                ctxMenu.onmouseleave = () => setGridRowContextMenu(undefined);
                return ctxMenu.removeEventListener("mouseleave", () => setGridRowContextMenu(undefined))
            }
        }
    }, [gridRowContextMenu])


    const savePluginSettingsWidth = useCallback(() => {
        let grid = document.getElementById(props.gridId) as HTMLElement;
        if (!grid) return;

        let columnCells = grid.getElementsByClassName(styles.gridColumnCell);
        let changed: boolean = false;

        const updatedColumns = pluginSettings.columns.map(column => {
            const headerCell = Array.from(columnCells).find(cell =>
                cell.getAttribute("data-property-name") === column.propertyName
            ) as HTMLElement;

            if (headerCell) {
                const currentWidth = headerCell.getBoundingClientRect().width;
                if (column.width !== currentWidth) {
                    changed = true;
                    return { ...column, width: currentWidth };
                }
            }
            return column;
        });

        if (changed) {
            setPluginSettings(prev => ({ ...prev, columns: updatedColumns }));
            setGridColumnsToSave({ columns: updatedColumns, save: true });
        }
    }, [props.gridId, pluginSettings.columns]);

    function renderSortIcon(item: IGridColumn) {
        if (item.displayName === '') return <div></div>
        if (initialLocalSortingData.fieldOrderBy === item.propertyName && initialLocalSortingData.orderDesc) return <DownIcon className={classNames(styles.sortIcon, styles.sortIcon_Selected)} />;

        if (initialLocalSortingData.fieldOrderBy === item.propertyName && !initialLocalSortingData.orderDesc) return <UpIcon className={classNames(styles.sortIcon, styles.sortIcon_Selected)} />;

        return <SortIcon className={styles.sortIcon} />;
    }

    const getStyleTd = (width: string, minWidth: string) => {
        return { width: width, minWidth: minWidth, height: pluginSettings.rowHeight }
    }

    const gridHeader = (
        <GridHeader
            gridId={props.gridId}
            headerRef={headerRef}
            totalTableWidth={totalTableWidth}
            rowHeight={rowHeight}
            multipleSelect={props.multipleSelect}
            typeChecked={typeChecked}
            allChecked={allChecked}
            setAllChecked={setAllChecked}
            numbering={props.numbering}
            setGridColumnsToSave={setGridColumnsToSave}
            pluginSettings={pluginSettings}
            filter={props.filter}
            filteredRows={filteredRows}
            data={data}
            initialLocalSortingData={initialLocalSortingData}
            setInitialLocalSortingData={setInitialLocalSortingData}
            setSortingStatus={setSortingStatus}
            setData={setData}
            compareByOrder={compareByOrder}
            sortArrayByField={sortArrayByField}
            searchRowsArray={searchRowsArray}
            setSearchRows={setSearchRows}
            renderSortIcon={renderSortIcon}
            savePluginSettingsWidth={savePluginSettingsWidth}
            handleColumnReorder={handleColumnReorder}
            searching={props.searching}
            draggable={true}
            onSort={props.onSort}
            onFilterDelete={props.onFilterDelete}
            onPageNumberChange={props.onPageNumberChange}
            onHeaderContextMenu={(e) => {
                if (process.env.REACT_APP_SHOW_GRID_CONTEXT === "true") {
                    e.preventDefault();
                    setHeaderCtxPosX(e.pageX);
                    setHeaderCtxPosY(e.pageY);
                    setGridHeaderContextMenuVisible(true);
                }
            }}
        />
    );

    const RowComponent = ({
        index,
        rows,
        pluginSettings,
        multipleSelect,
        numbering,
        renderCell,
        styles,
        setSelectedItem,
        style,
    }: RowComponentProps<IRowComponentData>) => {

        const item = rows[index];
        if (!item) return null;

        const cells = item.cells;
        let orderedCells: IGridCellWithWidth[] = [];
        for (let i = 0; i < cells.length; i++) {
            orderedCells.push({
                ...cells[i],
                order: pluginSettings.columns.find((col) => col.propertyName === cells[i].propertyName)?.order as number,
                width: pluginSettings.columns.find((col) => col.propertyName === cells[i].propertyName)?.width as number
            });
        }
        orderedCells = orderedCells.sort(compareByOrder);

        return (
            <tr
                key={item.idGlobal ?? index}
                data-item-id={item.idGlobal}
                data-selected={item.isSelected ? "true" : "false"}
                tabIndex={0}
                className={classNames(
                    styles.gridRow,
                    item.isSelected ? styles.gridRowSelected : null,
                    item.isDeleted ? styles.gridRowDeleted : null,
                    item.success ? styles.gridRowSuccess : styles.gridRowDeleted,
                    item.bunchType === 'Код товара' ? styles.gridRowGoodsCode : null,
                    item.bunchType === 'Штрихкод товара' ? styles.gridRowBarcode : null,
                    item.bunchType === 'Вручную' ? styles.gridRowHandBunch : null
                )}
                style={{
                    ...style,
                    height: rowHeight,
                    width: 'auto'
                }}
                onContextMenu={(e) => {
                    if (process.env.REACT_APP_SHOW_GRID_CONTEXT === "true") {
                        e.preventDefault();
                        setGridRowContextMenu(props.onGridRowContextMenuMouseClick?.(item, { x: e.pageX - 5, y: e.pageY - 5 }))
                    }

                    if (selectedItem) {
                        setHeaderCtxPosX(e.pageX);
                        setHeaderCtxPosY(e.pageY);
                        setGridRowContextMenuVisible(true);
                    }


                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex(index);

                    setTimeout(() => {
                        const currentRow = document.querySelector(`tr[data-item-id="${item.idGlobal}"]`) as HTMLElement;
                        if (currentRow) {
                            currentRow.focus();
                        }
                    }, 0);

                    if (props?.multipleSelect) {
                        if (props?.selectedItems?.length === 0) {
                            props.onMultipleSelect?.([item])
                        }
                        if (props?.selectedItems) {
                            props?.selectedItems?.find(x => x.idGlobal === item.idGlobal) === undefined &&
                                props.onMultipleSelect?.([...props.selectedItems, item])
                        }
                        if (item.isSelected && props?.selectedItems) {
                            const newSelecterArray = props?.selectedItems?.filter(x => x.idGlobal !== item.idGlobal)
                            props.onMultipleSelect?.([...newSelecterArray])
                        }
                    } else {
                        if (item.isSelected) {
                            setSelectedItem(undefined)
                            props.onMultipleSelect?.([])
                            props.onSelect?.(undefined)
                        } else {
                            setSelectedItem(item)
                            props.onSelect?.(item)
                            props.onMultipleSelect?.([item])
                        }
                    }
                }}
                onDoubleClick={() => {
                    props.multipleSelect === false && props.onDoubleClick?.(item)
                }}
            >
                {multipleSelect && (
                    <td className={styles.gridRowCell} style={getStyleTd("45px", "45px")}>
                        <input
                            type='checkbox'
                            checked={item.isSelected}
                            onChange={() => { }}
                            readOnly
                        />
                    </td>
                )}
                {numbering && (
                    <td className={styles.gridRowCell} style={getStyleTd("45px", "45px")}>
                        {index + 1}
                    </td>
                )}
                {orderedCells.map((cell, cellIndex) => {
                    const customRender = renderCell?.(cell, item, pluginSettings.rowHeight);
                    return (
                        cell.visibility && (
                            <td
                                key={`${cell.propertyName}_${item.idGlobal}_${cellIndex}`}
                                className={item.success ? styles.gridRowCellSuccess : styles.gridRowCell}
                                style={{
                                    width: cell.width,
                                    minWidth: cell.width,
                                    height: rowHeight
                                }}
                                data-order-number={cell.order}
                                data-property-name={cell.propertyName}
                            >
                                {customRender !== undefined && customRender !== null
                                    ? customRender
                                    : (cell.multipleValue ? (
                                        <div className={(cell.propertyName === "goodsBarcode" && cell.value[1] === null)
                                            ? styles.outWrapper_singleData
                                            : styles.outWrapper}
                                        >
                                            <span className={styles.gridRowCellText}>
                                                <OverflowTooltip
                                                    className={styles.gridRowCellText}
                                                    title={cell.value[0]}
                                                >
                                                    {cell.value[0]}
                                                </OverflowTooltip>
                                            </span>

                                            <OverflowTooltip
                                                className={styles.gridRowCellText}
                                                title={cell.value[1]}
                                            >
                                                {cell.value[1]}
                                            </OverflowTooltip>

                                        </div>
                                    ) : (
                                        <div className={styles.outWrapper_singleData}>
                                            <span
                                                className={typeof cell.value === "string" || typeof cell.value === "number"
                                                    ? cell.propertyName === 'bunchType'
                                                        ? styles.singleTextField
                                                        : styles.gridRowCellText_singleData
                                                    : typeof cell.value === 'boolean'
                                                        ? styles.singleTextField
                                                        : styles.gridRowCellText
                                                }
                                            >
                                                <OverflowTooltip
                                                    className={styles.gridRowCellText}
                                                    title={cell.value}
                                                >
                                                    {
                                                        typeof cell.value === 'boolean'
                                                            ? <input type="checkbox" checked={cell.value} readOnly />
                                                            : cell.value
                                                    }
                                                </OverflowTooltip>
                                            </span>
                                        </div>
                                    ))
                                }
                            </td>
                        )
                    );
                })}
            </tr>
        );
    };

    const renderVirtualizedGrid = () => {
        const containerHeight = clientHeight || 440;
        const listHeight = Math.min(filteredRows.length * rowHeight, containerHeight - rowHeight);

        const rowData: IRowComponentData = {
            rows: filteredRows,
            pluginSettings,
            multipleSelect: props.multipleSelect,
            numbering: props.numbering,
            selectedItem,
            selectedItems: props.selectedItems,
            onRowClick: (item) => { },
            onRowDoubleClick: (item) => {
                props.multipleSelect === false && props.onDoubleClick?.(item);
            },
            onKeyDown: (e, item) => { },
            onContextMenu: (e, item) => {
                if (process.env.REACT_APP_SHOW_GRID_CONTEXT === "true") {
                    e.preventDefault();
                    setGridRowContextMenu(
                        props.onGridRowContextMenuMouseClick?.(item, {
                            x: e.pageX - 5,
                            y: e.pageY - 5
                        })
                    );
                }
                if (selectedItem) {
                    setHeaderCtxPosX(e.pageX);
                    setHeaderCtxPosY(e.pageY);
                    setGridRowContextMenuVisible(true);
                }
            },
            renderCell: props.renderCell,
            getStyleTd,
            styles,
            setSelectedItem,
            onSelect: props.onSelect,
            onMultipleSelect: props.onMultipleSelect
        };

        return (
            <div
                className={styles.gridScrollContainer}
                ref={tableRef}
            >
                <div
                    className={styles.gridHeaderWrapper}
                    style={{
                        transform: `translateX(-${scrollLeft}px)`
                    }}
                >
                    {gridHeader}
                </div>
                <VirtualGrid
                    style={{
                        height: listHeight,
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                    rowComponent={RowComponent}
                    rowCount={filteredRows.length}
                    rowHeight={rowHeight}
                    rowProps={rowData}
                    overscanCount={5}
                    activeIndex={activeIndex}
                    onScroll={handleGridScroll}
                    loading={props.loadingStatus === LoadingStatus.InProcess}
                >
                </VirtualGrid>
            </div>
        );
    };

    const render = () => {
        if (props.loadingStatus === LoadingStatus.NoAccess) {
            return <div className={styles.noDataInfo}>
                <span>{baseT("noAccess")}</span>
            </div>
        } else if (props.loadingStatus === LoadingStatus.NoData) {
            return (
                <div className={styles.noDataInfo}>
                    <span>{t('general.noData')}</span>
                </div>
            )
        } else {
            return <div className={styles.gridWrapper} ref={gridContainerRef}>
                <FlexRow className={styles.rowAboveTable}>
                    <div className={styles.gridWrapper__quantityPositions}>{baseT("quantityPositions")}: {data.length}</div>
                    <div className={styles.gridWrapper__quantityPositions}>{baseT("quantityNotConnectedPositions")}: {data.filter((element) => !element.connected).length}</div>
                    <div className={styles.gridWrapper__checkbox}>
                        <CheckBox
                            id={uuidv4()}
                            label={baseT("onlyNotConnectedPositions")}
                            defaultChecked={onlyСonnected}
                            onChanged={(checked) => {
                                setOnlyСonnected(checked);
                            }}
                        />
                    </div>
                    <div className={styles.gridWrapper__checkbox}>
                        <CheckBox
                            id='toggleSortDate'
                            label={baseT("connectedPositionsToEnd")}
                            defaultChecked={toggleSortDate}
                            onChanged={setToggleSortDate}
                        />
                    </div>
                    {!props.hideSettingstButton && (
                        <div className={styles.buttonsContainer}>
                            <div className={styles.buttonLink} onClick={() => setShowGridHeaderSettings(true)}>{baseT("gridSettings")}</div>
                        </div>
                    )}
                </FlexRow>

                {renderVirtualizedGrid()}

                {props?.separator && <div onMouseDown={onMouseHoldDown} className={classNames(defaultStyles.separator, defaultStyles.separator_horizontal)}></div>}
                {
                    !props.hiddenPagination &&
                    <GridPaginator
                        nearPage={2}
                        pageNumber={props.filter.pageNumber}
                        totalCount={props.totalCount}
                        numberPerPage={props.filter.numberPerPage}
                        onPageNumberChange={props.onPageNumberChange}
                        onNumberPerPageChange={props.onNumberPerPageChange}
                    />
                }
                {
                    gridHeaderContextMenuVisible &&
                    <ContextMenu
                        id={getContextId('header', props.gridId)}
                        xPos={headerCtxPosX - 5}
                        yPos={headerCtxPosY - 5}
                        items={[{
                            name: baseT("gridSettings"),
                            onClick: () => setShowGridHeaderSettings(true)
                        }
                        ]}>
                    </ContextMenu>
                }
                {
                    gridRowContextMenuVisible &&
                    <ContextMenu
                        id={getContextId('row', props.gridId)}
                        xPos={headerCtxPosX - 5}
                        yPos={headerCtxPosY - 5}
                        items={props.contextMenuItems ?? []}>
                    </ContextMenu>
                }
                {
                    showGridHeaderSettings &&
                    <GridUISettings
                        plugin={pluginSettings}
                        defaultPlugin={props.plugin}
                        hideRowHeightSetting={true}
                        save={(e) => {
                            setGridColumnsToSave({ columns: e.columns, save: true });
                            setPluginSettings({ ...pluginSettings, rowHeight: rowHeight })
                            setGridHeaderContextMenuVisible(false)
                            setShowGridHeaderSettings(false)
                        }}
                        cancel={() => {
                            setGridHeaderContextMenuVisible(false)
                            setShowGridHeaderSettings(false)
                        }}>
                    </GridUISettings>
                }

                {
                    gridRowContextMenu
                }
            </div>
        }
    }
    return (
        render()
    )
}

export default memo(BunchGrid)